/**
 * Townsfolk at rest and at work (drawn on demand, see `SpriteSet.extra`).
 */
import { gait, keyed, WALK_FRAMES, type PoseRigs } from './kit';

/** This group's poses. */
export type Poses =
  | 'glance'
  | 'stretch'
  | 'wave'
  | 'bow'
  | 'cower'
  | 'fish'
  | 'hoe'
  | 'sweep'
  | 'haul'
  | 'mend'
  | 'drink'
  | 'call';

export const POSES: PoseRigs<Poses> = {
  glance: (ph) => {
    // Looking round while standing about: the head turned one way (0) or the other (1).
    return { f1: -2, f2: 1, headDx: ph ? -2 : 2 };
  },
  stretch: () => {
    // A long stretch, arms up, eyes shut.
    return { f1: -2, f2: 1, tilt: -10, arm: 'overhead', far: 'up', closed: true };
  },
  wave: (ph) => {
    // A hand up high, swung side to side.
    return { f1: -2, f2: 1, face: 'cast', ...keyed([{ arm: 'overhead' }, { arm: 'raise' }], ph) };
  },
  bow: () => {
    // Fist in palm at the chest (抱拳), bending a little from the waist.
    return {
      o: 2,
      lean: 1,
      tilt: 18,
      headDx: 1,
      f1: -1,
      f2: 1,
      arm: 'clasp',
      far: 'clasp',
      closed: true,
    };
  },
  cower: () => {
    // Crouched, arms up over the head, leaning away.
    return {
      o: 7,
      lean: -3,
      tilt: 14,
      headDx: -1,
      f1: -4,
      f2: 3,
      arm: 'guard',
      far: 'up',
      face: 'block',
      sway: -1,
    };
  },
  fish: () => {
    // Rod held out over the water, the other hand on the butt.
    return { f1: -3, f2: 2, tilt: 4, arm: 'out', far: 'fwd' };
  },
  hoe: (ph) => {
    // Raised high (0), then driven into the soil (1).
    return ph
      ? { o: 4, lean: 1, tilt: 22, headDx: 1, f1: -6, f2: 5, arm: 'low', far: 'fwd', sway: 1 }
      : { o: 0, lean: -1, tilt: -8, f1: -6, f2: 5, arm: 'overhead', far: 'up', sway: -0.5 };
  },
  sweep: (ph) => {
    // The broom swept forward (0) and drawn back (1).
    return ph
      ? { o: 2, lean: 1, tilt: 8, f1: -5, f2: 4, arm: 'lowBack', far: 'fwd', sway: -0.5 }
      : { o: 2, lean: 1, tilt: 12, f1: -5, f2: 4, arm: 'low', far: 'fwd', sway: 0.5 };
  },
  haul: (ph) => {
    // Walking under a shoulder pole: shorter, bouncing steps, a hand steadying the pole.
    const { f1, l1, f2, l2, o } = gait(ph / WALK_FRAMES, 6.5, 0.4, 4);
    return { f1, f2, l1, l2, o: o + 1, lean: 1, tilt: 4, sway: 0.8, arm: 'raise' };
  },
  mend: () => {
    // Sitting with a net across the lap, looking down at the work.
    return { o: 12, tilt: 14, headDx: 1, arm: 'sit', far: 'fwd' };
  },
  drink: () => {
    // Sitting, cup raised to the lips.
    return { o: 12, tilt: -6, arm: 'raise' };
  },
  call: () => {
    // A hand to the mouth, the other arm waving passers-by in.
    return { f1: -2, f2: 2, tilt: -4, arm: 'raise', far: 'up', face: 'atk' };
  },
};
